import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { Button, Input, Modal, cn } from '@immo/ui-kit';
import { insertNodesAt, trackCaret, type ChipInsertHandle } from '@/components/email-template/email-template-chips';
import { attachOpenFieldInteraction } from '@/components/email-template/open-field-interaction';
import { PaperclipIcon } from '@/components/layout/icons';

export type ComposerBodyEditorHandle = ChipInsertHandle;

export const TOOLBAR_INSERT_ACTION_CLASS =
  'inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-sm text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary focus-visible:outline-none focus-visible:shadow-focus';

export type ComposerBodyEditorProps = {
  // Accessible name for the editable area (kept as the body's a11y label).
  readonly label: string;
  // Fires with the editor's current HTML on every edit / formatting command.
  readonly onChange: (html: string) => void;
  // Opens the attachment file picker (owned by the parent composer). Without it there is no paperclip.
  readonly onAttach?: () => void;
  // Horizontal padding matching the composer container, so the toolbar rule and
  // body align with the header field rows above.
  readonly padX: string;
  // Translations for the link dialog (the parent composer's namespace).
  readonly t: (key: string, values?: Record<string, string | number>) => string;
  // Ghost text shown while the editor is empty (e.g. "Antwort schreiben …").
  readonly placeholder?: string;
  // Controls at the right end of the toolbar (the E-Mail-Vorlage's insert actions).
  readonly toolbarEnd?: ReactNode;
  // Applied once on mount; re-assigning innerHTML on every edit would reset the caret.
  readonly defaultHtml?: string;
  readonly invalid?: boolean;
  readonly describedBy?: string;
  readonly onFocus?: () => void;
  // Rendered under the editable area, inside its scroll, outside the contentEditable — the composer's read-only signature.
  readonly belowBody?: ReactNode;
};

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(text: string): string {
  return escapeHtml(text).replace(/"/g, '&quot;');
}

// The <a> containing a node (walking up), or null — lets a caret/click inside an
// existing link open the dialog to edit it whole.
function closestAnchor(node: Node | null): HTMLAnchorElement | null {
  let el: Element | null = node instanceof Element ? node : (node?.parentElement ?? null);

  while (el !== null) {
    if (el instanceof HTMLAnchorElement) return el;
    el = el.parentElement;
  }

  return null;
}

// No dedicated list / link glyphs in the icon set — small inline SVGs instead.
function ListIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden className={className}>
      <circle cx="2.5" cy="4" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="2.5" cy="8" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="2.5" cy="12" r="0.9" fill="currentColor" stroke="none" />
      <path d="M6 4h8M6 8h8M6 12h8" strokeLinecap="round" />
    </svg>
  );
}

function LinkIcon({ className }: { readonly className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden className={className}>
      <path d="M6.5 9.5 9.5 6.5" strokeLinecap="round" />
      <path d="M7.5 4.5 8.8 3.2a2.4 2.4 0 0 1 3.4 3.4L10.9 7.9" strokeLinecap="round" />
      <path d="M8.5 11.5 7.2 12.8a2.4 2.4 0 0 1-3.4-3.4L5.1 8.1" strokeLinecap="round" />
    </svg>
  );
}

function ToolbarButton({
  onClick,
  label,
  children,
}: {
  readonly onClick: () => void;
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    // The kit `Button` in `ghost` / `icon`, pinned back from 36px to the 28px
    // toolbar footprint the composer's `py-1.5` rule is built around
    // (IMM-741.5).
    //
    // `onMouseDown` + `preventDefault` is the whole reason this control is not a
    // plain `onClick`: mousedown is what would blur the `contentEditable` body,
    // and a blurred editor has no selection left for `document.execCommand` to
    // format. Pressing „Fett" would then still look right and apply bold to
    // nothing. The primitive changed; this handler must not.
    <Button
      type="button"
      variant="ghost"
      size={null}
      onMouseDown={(event) => {
        event.preventDefault();
        onClick();
      }}
      aria-label={label}
      title={label}
      className="h-7 w-7 shrink-0 text-text-secondary hover:text-text-primary"
    >
      {children}
    </Button>
  );
}

/**
 * The rich-text message body (design): a small formatting toolbar separated from
 * the subject above and the body below by grey rules, over a contentEditable area.
 * Formatting uses `document.execCommand` — deprecated but adequate here; the parent
 * reads the HTML via `onChange` and assembles it (+ signature + quote) on send.
 */
export const ComposerBodyEditor = forwardRef<ComposerBodyEditorHandle, ComposerBodyEditorProps>(
  function ComposerBodyEditor(
    {
      label,
      onChange,
      onAttach,
      padX,
      t,
      placeholder,
      toolbarEnd,
      defaultHtml,
      invalid,
      describedBy,
      onFocus,
      belowBody,
    },
    handle,
  ) {
    const ref = useRef<HTMLDivElement>(null);
    const initialHtml = useRef(defaultHtml);
    // The last caret inside the editor: a menu or dialog opened from the toolbar takes focus, so only
    // a saved range survives to the moment something is inserted.
    const caretRange = useRef<Range | null>(null);
    // The editor's selection is lost once the link dialog steals focus — remember it
    // so the inserted link lands where the caret was.
    const savedRange = useRef<Range | null>(null);
    // The <a> being edited (caret/click inside an existing link), or null for a new one.
    const editingAnchor = useRef<HTMLAnchorElement | null>(null);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const emit = () => {
      if (ref.current !== null) onChange(ref.current.innerHTML);
    };

    // The interaction is attached once; this keeps it calling the latest `onChange`.
    const emitRef = useRef(emit);

    useEffect(() => {
      emitRef.current = emit;
    });

    useEffect(() => {
      const root = ref.current;

      if (root === null) return undefined;

      if (initialHtml.current !== undefined) root.innerHTML = initialHtml.current;

      const detachCaret = trackCaret(root, (range) => {
        caretRange.current = range;
      });
      const detachOpenFields = attachOpenFieldInteraction(root, () => emitRef.current());

      return () => {
        detachCaret();
        detachOpenFields();
      };
    }, []);

    useImperativeHandle(handle, () => ({
      focus: () => ref.current?.focus(),
      insertNodesAtCaret: (nodes) => {
        if (ref.current === null) return;

        caretRange.current = insertNodesAt(ref.current, caretRange.current, nodes);
        emit();
      },
      root: () => ref.current,
    }));

    const exec = (command: string, value?: string) => {
      ref.current?.focus();
      document.execCommand(command, false, value);
      emit();
    };

    // Open the dialog editing `anchor` (text + href prefilled), or for a new link at
    // `range` prefilled with `initialText`.
    const openLinkEditor = (anchor: HTMLAnchorElement | null, range: Range | null, initialText: string) => {
      editingAnchor.current = anchor;
      savedRange.current = range;
      setLinkText(anchor !== null ? (anchor.textContent ?? '') : initialText);
      setLinkUrl(anchor !== null ? (anchor.getAttribute('href') ?? '') : '');
      setLinkOpen(true);
    };

    // Toolbar button: edit the link under the caret if there is one, else start a new
    // one prefilled with the selected text.
    const openLinkModal = () => {
      const selection = window.getSelection();
      const range = selection !== null && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const inEditor = range !== null && ref.current !== null && ref.current.contains(range.commonAncestorContainer);
      const anchor = inEditor ? closestAnchor(selection?.anchorNode ?? null) : null;

      openLinkEditor(anchor, inEditor && range !== null ? range.cloneRange() : null, selection?.toString() ?? '');
    };

    // Clicking an existing link opens the dialog to edit it whole (text + href).
    const onEditorClick = (event: ReactMouseEvent<HTMLDivElement>) => {
      const anchor = closestAnchor(event.target as Node);

      if (anchor === null || ref.current === null || !ref.current.contains(anchor)) return;

      openLinkEditor(anchor, null, '');
    };

    const closeLink = () => {
      editingAnchor.current = null;
      setLinkOpen(false);
    };

    const applyLink = () => {
      const url = linkUrl.trim();

      if (url === '') return;

      const text = linkText.trim() === '' ? url : linkText.trim();
      const anchor = editingAnchor.current;

      if (anchor !== null) {
        // Editing an existing link — update it in place (text + href).
        anchor.setAttribute('href', url);
        anchor.textContent = text;
      } else {
        ref.current?.focus();

        // Restore the caret/selection captured before the dialog opened.
        if (savedRange.current !== null) {
          const selection = window.getSelection();

          selection?.removeAllRanges();
          selection?.addRange(savedRange.current);
        }

        document.execCommand('insertHTML', false, `<a href="${escapeAttr(url)}">${escapeHtml(text)}</a>`);
      }

      editingAnchor.current = null;
      emit();
      setLinkOpen(false);
    };

    return (
      <>
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar — subtle canvas fill, grey rule beneath separating it (and the subject
          above) from the body. */}
          <div className={cn('flex items-center gap-0.5 border-b border-border bg-canvas py-1.5', padX)}>
            <ToolbarButton onClick={() => exec('bold')} label="Fett">
              <span className="text-base font-bold">B</span>
            </ToolbarButton>
            <ToolbarButton onClick={() => exec('italic')} label="Kursiv">
              <span className="text-base italic">I</span>
            </ToolbarButton>
            <ToolbarButton onClick={() => exec('underline')} label="Unterstrichen">
              <span className="text-base underline">U</span>
            </ToolbarButton>
            <span className="mx-1 h-4 w-px bg-border" aria-hidden />
            <ToolbarButton onClick={() => exec('insertUnorderedList')} label="Liste">
              <ListIcon className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton onClick={openLinkModal} label="Link">
              <LinkIcon className="h-4 w-4" />
            </ToolbarButton>
            {onAttach !== undefined && (
              <>
                <span className="mx-1 h-4 w-px bg-border" aria-hidden />
                <ToolbarButton onClick={onAttach} label="Anhang">
                  <PaperclipIcon className="h-4 w-4" />
                </ToolbarButton>
              </>
            )}
            {toolbarEnd !== undefined && (
              <>
                <span className="flex-1" />
                {toolbarEnd}
              </>
            )}
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div
              ref={ref}
              id="composer-body"
              role="textbox"
              aria-multiline="true"
              aria-label={label}
              contentEditable
              suppressContentEditableWarning
              aria-invalid={invalid}
              {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
              onInput={emit}
              onClick={onEditorClick}
              {...(onFocus !== undefined ? { onFocus } : {})}
              {...(placeholder !== undefined ? { 'data-placeholder': placeholder } : {})}
              className={cn(
                // Restore list rendering — Tailwind's preflight strips ul/ol styling, so an
                // inserted list is otherwise invisible.
                'min-h-[12rem] w-full grow shrink-0 py-3 text-base-sm text-text-primary outline-none',
                '[&_a]:text-interactive [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6',
                // Ghost placeholder while empty (contentEditable has no native one).
                'empty:before:pointer-events-none empty:before:text-text-placeholder empty:before:content-[attr(data-placeholder)]',
                padX,
              )}
            />
            {belowBody}
          </div>
        </div>

        {linkOpen && (
          <Modal
            open
            onClose={closeLink}
            size="md"
            title={t('link-title')}
            confirmOnClose={false}
            footer={
              <>
                <Button type="button" variant="ghost" onClick={closeLink}>
                  {t('link-cancel')}
                </Button>
                <Button type="button" variant="primary" disabled={linkUrl.trim() === ''} onClick={applyLink}>
                  {t('link-insert')}
                </Button>
              </>
            }
          >
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs text-text-secondary">{t('link-text-label')}</span>
                <Input value={linkText} onChange={(event) => setLinkText(event.target.value)} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-text-secondary">{t('link-url-label')}</span>
                <Input value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} placeholder="https://…" />
              </label>
            </div>
          </Modal>
        )}
      </>
    );
  },
);
